import { atom } from "jotai";
import { RouterProvider, createHashRouter, redirect } from "react-router-dom";
import Summary from "./routes/Summary";
import ThankYou from "./routes/ThankYou";
import Fail from "./routes/SendFailed";
import Root from "./routes/root";
import Intro from "./routes/Intro";
import {
  FormModel,
  SelectedJourney,
  TravelReturnSearchModel,
  TravelSearchModel,
} from "./types/FormTypes";
import ContactDetails from "./routes/ContactDetails";
import TravelSearch from "./routes/TravelSearch";
import TravelSummary from "./routes/TravelSummary";
import TravelReturnSearch from "./routes/TravelSearchReturn";
import UnregisterTravelPlan from "./routes/UnregisterTravelPlan";
import UnregisterTravelSuccess from "./routes/UnregisterTravelSuccess";
import UnregisterTravelError from "./routes/UnregisterTravelError";

require("./extensions/yup-extensions");

// Store - Collecting Travel Search Data

export const travelSearchAtom = atom<TravelSearchModel>({
  selectedJourneyFrom: null,
  selectedJourneyTo: null,
  journeyDate: "",
  journeyTime: "",
});

export const travelReturnSearchAtom = atom<TravelReturnSearchModel>({
  selectedReturnJourneyFrom: null,
  selectedReturnJourneyTo: null,
  returnJourneyDate: "",
  returnJourneyTime: "",
});

// Store - Collecting FormData

export const formDataAtom = atom<FormModel>({
  schoolorg: "",
  contactperson: "",
  phonenumberschoolorg: "",
  mailadress: "",
  journey: "",
  returnJourney: "",
  traveltype: "",
  // TEMP
  travelQty: "",
  phonenumber: "",

  selectedJourney: {
    fromName: "",
    toName: "",
    date: "",
    travelTime: "",
    journeyKey: "",
    line: [],
    switches: 0,
    unformattedDate: "",
  },
  selectedReturnJourney: {
    fromName: "",
    toName: "",
    date: "",
    travelTime: "",
    journeyKey: "",
    line: [],
    switches: 0,
    unformattedDate: "",
  },

  // Test components
  filesuploadtest: [],
  textareatest: "",
  inputtest: "",
  radiotest: "",
  testpicker: "",
});

export const selectedJourneyAtom = atom<SelectedJourney>({
  fromName: "",
  toName: "",
  date: "",
  travelTime: "",
  journeyKey: "",
  line: [],
  switches: 0,
  unformattedDate: "",
});

export const selectedReturnJourneyAtom = atom<SelectedJourney>({
  fromName: "",
  toName: "",
  date: "",
  travelTime: "",
  journeyKey: "",
  line: [],
  switches: 0,
  unformattedDate: "",
});

// Flow guard and router setup
const FLOW_KEY = "group-travel-flow";

const navigationEntry = performance.getEntriesByType("navigation")[0] as
  | PerformanceNavigationTiming
  | undefined;

const isReload = navigationEntry?.type === "reload";

if (isReload) {
  sessionStorage.removeItem(FLOW_KEY);
}

function startFlow() {
  sessionStorage.setItem(FLOW_KEY, "active");
  return null;
}

function isFlowActive() {
  return sessionStorage.getItem(FLOW_KEY) === "active";
}

function clearFlow() {
  sessionStorage.removeItem(FLOW_KEY);
  return null;
}

function requireActiveFlow() {
  return isFlowActive() ? null : redirect("/");
}

function createProtectedStep(path: string, element: JSX.Element) {
  return {
    path,
    element,
    loader: async () => {
      return requireActiveFlow();
    },
  };
}

const router = createHashRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      {
        index: true,
        element: <Intro />,
        loader: () => {
          return startFlow();
        },
      },

      createProtectedStep("vart-vill-ni-resa", <TravelSearch />),
      createProtectedStep("valj-aterresa", <TravelReturnSearch />),
      createProtectedStep("er-resa", <TravelSummary />),
      createProtectedStep("kontaktuppgifter", <ContactDetails />),
      createProtectedStep("summering", <Summary />),

      {
        path: "tack-sida",
        element: <ThankYou />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },
      {
        path: "ett-fel-intraffade",
        element: <Fail />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },

      {
        path: "avboka-resa",
        element: <UnregisterTravelPlan />,
        loader: async () => {
          return { title: "Avanmäl gruppresa" };
        },
      },
      {
        path: "avboka-resa-gemomford",
        element: <UnregisterTravelSuccess />,
        loader: async () => {
          return { title: "Avanmäl gruppresa" };
        },
      },
      {
        path: "avboka-resa-fel",
        element: <UnregisterTravelError />,
        loader: async () => {
          return { title: "Avanmäl gruppresa" };
        },
      },
    ],
  },
]);

// Count pages
const pagePaths =
  router?.routes[0]?.children?.map((child) => child.path).filter(Boolean) || [];

export const pageCountAtom = atom({
  pagePaths,
});

function App() {
  return (
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  );
}

export default App;
